// HorizontalWorkflowStepper.jsx
import React from "react";
import { Box, Typography } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";

const WORKFLOW_STEPS = [
  {
    id: 1,
    title: "RFP summarization",
    description: "AI extracts and summarizes RFP content",
  },
  {
    id: 2,
    title: "RFP completeness check",
    description: "Validate required information completeness",
  },

  {
    id: 3,
    title: "Carrier translation",
    description: "AI normalizes carrier plan structure",
  },
  {
    id: 4,
    title: "Quote information consolidation",
    description: "AI consolidates quote information",
  },
];

const HorizontalWorkflowStepper = ({ activeStep = 3 }) => {
  /* 
    activeStep:
      1–5 = current active step
      Steps < activeStep = completed
      Steps > activeStep = upcoming
  */

  return (
    <Box
      sx={{
        position: "fixed",
        ml: 1,
        p: 2,
        mt: -1,
        bgcolor: "white",
        width: "100vw",
        boxShadow: "0 8px 20px rgba(15, 23, 42, 0.3)",
        mb: 4,
        zIndex: 1000,
        mx: "auto",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "stretch",
          justifyContent: "space-between",
          gap: 0,
        }}
      >
        {WORKFLOW_STEPS.map((step, index) => {
          const isCompleted = step.id < activeStep;
          const isActive = step.id === activeStep;

          return (
            <React.Fragment key={step.id}>
              {/* STEP ITEM */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  flex: 1,
                  minWidth: 0,
                }}
              >
                {/* Top row: circle & text */}
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                  {/* Circle */}
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      border: isActive
                        ? "2px solid"
                        : isCompleted
                        ? "none"
                        : "1px solid",
                      borderColor: isActive ? "primary.main" : "divider",
                      bgcolor: isCompleted
                        ? "primary.main"
                        : isActive
                        ? "white"
                        : "action.hover",
                      color: isCompleted
                        ? "primary.contrastText"
                        : isActive
                        ? "primary.main"
                        : "text.secondary",
                      fontSize: 14,
                      fontWeight: 700,
                    }}
                  >
                    {isCompleted ? (
                      <CheckIcon sx={{ fontSize: 18 }} />
                    ) : (
                      step.id
                    )}
                  </Box>

                  {/* Text */}
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: isActive ? 700 : 600,
                        color:
                          isCompleted || isActive
                            ? "text.primary"
                            : "text.secondary",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                      }}
                    >
                      {step.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.secondary",
                        mt: 0.25,
                        fontSize: "0.8rem",
                      }}
                    >
                      {step.description}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* CONNECTOR (except after last step) */}
              {index < WORKFLOW_STEPS.length - 1 && (
                <Box
                  sx={{
                    width: 32,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {/* optional connector line */}
                </Box>
              )}
            </React.Fragment>
          );
        })}
      </Box>
    </Box>
  );
};

export default HorizontalWorkflowStepper;
